import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import TextLink from 'components/TextLink';
import WidgetContainer from 'components/widget/WidgetContainer';
import BodyText from 'components/widget/BodyText';
import getBoxSizeString from 'components/helpers/getBoxSizeString';
import { FullWidthPaperContent } from 'components/Paper';
import Address from 'components/Address';
import ProductImageView from 'components/ProductImageView';
import ordersRoutePath from 'containers/paths/OrdersContainerPath';
import { renderPackActions } from 'helpers/packHelpers';

const Wrapper = styled.div`
  .orderHeading {
    margin-bottom: ${({ theme }) => theme.spacing * 3}px;
  }
`;

const OrderWidget = ({
  className, pack, canChangeAddress,
}) => {
  const classes = ['OrderWidget'];
  if (className) classes.push(className);

  const headingDictionary = {
    Pending: 'Upcoming delivery',
    Processing: 'Processing delivery',
    Dispatched: moment(pack.deliveryDate).isBefore() ? 'Previous delivery' : 'Dispatched, delivery due',
    Completed: 'Previous delivery',
    Cancelled: 'Skipped delivery',
  };

  return (
    <Wrapper className={classes.join(' ')}>
      <div className="orderHeading">
        <h4>{headingDictionary[pack.status]}</h4>
        <h1>{moment(pack.deliveryDate).format('DD MMM YYYY')}</h1>

        {renderPackActions(pack)}
      </div>
      <WidgetContainer>
        <h4>Your package</h4>
        <h2>{pack.product.packSize.usage}</h2>
        <BodyText>{getBoxSizeString(pack.product.packSize, pack.product.hasSolution)}</BodyText>

        <ProductImageView format="thumbnailAndText" items={pack.product.items} />

        {pack.status !== 'Cancelled' && (
          <FullWidthPaperContent className="section">
            {['Completed', 'Dispatched'].includes(pack.status)
              ? <h4>This delivery was sent to</h4>
              : <h4>This delivery will be sent to</h4>}
            <Address address={pack.deliveryAddress} />
            {canChangeAddress && (
              pack.status === 'Pending' && <TextLink props={{ to: ordersRoutePath(pack.id, 'changeAddress') }} rightChevron>Change address for this delivery</TextLink>
            )}
          </FullWidthPaperContent>
        )}
      </WidgetContainer>
    </Wrapper>
  );
};

OrderWidget.displayName = 'OrderWidget';

OrderWidget.propTypes = {
  className: PropTypes.string,
  canChangeAddress: PropTypes.bool,
  pack: PropTypes.shape({
    storeUrl: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    previousPack: PropTypes.shape({}),
    nextPack: PropTypes.shape({}),
    status: PropTypes.oneOf([
      'Pending',
      'Processing',
      'Dispatched',
      'Completed',
      'Cancelled',
    ]),
    availableActions: PropTypes.arrayOf(PropTypes.string),
    deliveryDate: PropTypes.string,
    product: PropTypes.shape({
      hasSolution: PropTypes.bool,
      items: PropTypes.arrayOf(PropTypes.shape({})),
      packSize: PropTypes.shape({
        subTitle: PropTypes.string,
        usage: PropTypes.string,
      }),
    }),
    deliveryAddress: PropTypes.shape({}),
  }),
};

OrderWidget.defaultProps = {
  className: null,
  pack: null,
  canChangeAddress: false,
};

export default OrderWidget;
